'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { settingAPI, menuAPI, productAPI, contactAPI } from '@/lib/api';
import Link from 'next/link';

export default function Footer() {
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      try {
        return await settingAPI.getAll();
      } catch (error) {
        console.error('[Footer] Failed to fetch settings:', error);
        return {};
      }
    },
    retry: 1,
    retryDelay: 1000,
    throwOnError: false,
  });

  const { data: footerMenus } = useQuery({
    queryKey: ['menus', 'footer'],
    queryFn: async () => {
      try {
        return await menuAPI.getList('footer');
      } catch (error) {
        console.error('[Footer] Failed to fetch footer menus:', error);
        return { data: [] };
      }
    },
    retry: 1,
    retryDelay: 1000,
    throwOnError: false,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['product-categories'],
    queryFn: async () => {
      try {
        return await productAPI.getCategories();
      } catch (error) {
        console.error('[Footer] Failed to fetch product categories:', error);
        return { data: [] };
      }
    },
    retry: 1,
    retryDelay: 1000,
    throwOnError: false,
  });

  // settingAPI.getAll() 现在直接返回 Settings 对象
  const settingsData = settings || {};
  const footerMenu = footerMenus?.data?.[0];
  const productCategories = categoriesData?.data || [];

  // 过滤出状态为active的菜单项
  const filterActiveItems = (items: any[]): any[] => {
    if (!items || items.length === 0) return [];
    return items
      .filter(item => item.status === 'active')
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      .map(item => ({
        ...item,
        children: item.children && item.children.length > 0 ? filterActiveItems(item.children) : undefined,
      }));
  };

  const activeMenuItems = footerMenu?.items && Array.isArray(footerMenu.items) && footerMenu.items.length > 0
    ? filterActiveItems(footerMenu.items)
    : [];

  // 获取About Us菜单项
  const aboutUsMenu = activeMenuItems.find((item: any) => item.title === 'About Us' || item.url?.includes('about-us'));
  const aboutUsChildren = aboutUsMenu?.children || [];

  // 获取News菜单项
  const newsMenu = activeMenuItems.find((item: any) => item.title === 'News' || item.url?.includes('news'));
  const newsChildren = newsMenu?.children || [];

  // 顶级产品分类
  const topLevelCategories = productCategories.filter((cat: any) => !cat.parentId);

  return (
    <footer>
      <style dangerouslySetInnerHTML={{
        __html: `
          .elementor-element-f852bb5 .elementor-container.elementor-column-gap-default,
          .elementor-element-be8562e .elementor-container.elementor-column-gap-default {
            max-width: 1200px !important;
            margin-left: auto !important;
            margin-right: auto !important;
            padding-left: 20px !important;
            padding-right: 20px !important;
            box-sizing: border-box !important;
            width: 100% !important;
          }
          .elementor-element-2436317 {
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          .elementor-element-858a311 {
            max-width: 100% !important;
            width: 100% !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            box-sizing: border-box !important;
          }
          .elementor-element-858a311 .elementor-container.elementor-column-gap-default {
            max-width: 100% !important;
            width: 100% !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            box-sizing: border-box !important;
          }
          .elementor-element-4860806 .elementor-container.elementor-column-gap-default {
            max-width: 1200px !important;
            margin-left: auto !important;
            margin-right: auto !important;
            padding-left: 20px !important;
            padding-right: 20px !important;
            box-sizing: border-box !important;
            width: 100% !important;
          }
          .elementor-element-01d2b44 {
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          .elementor-element-01d2b44 .elementor-widget-wrap {
            max-width: 100% !important;
            width: 100% !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            box-sizing: border-box !important;
          }
          #ast-desktop-header .ast-container {
            max-width: 1200px !important;
            margin-left: auto !important;
            margin-right: auto !important;
            padding-left: 20px !important;
            padding-right: 20px !important;
            box-sizing: border-box !important;
            width: 100% !important;
          }
        `
      }} />
      <div data-elementor-type="footer" data-elementor-id="334" className="elementor elementor-334 elementor-location-footer" data-elementor-post-type="elementor_library">
        {/* 第一部分：GET an Instant Quote + 联系表单 */}
        <section className="elementor-section elementor-top-section elementor-element elementor-element-f852bb5 elementor-section-boxed elementor-section-height-default elementor-section-height-default" data-id="f852bb5" data-element_type="section" data-settings='{"background_background":"classic"}' style={{ backgroundColor: '#1D2A5D', width: '100%', maxWidth: '100%', minWidth: '100%' }}>
          <div className="elementor-container elementor-column-gap-default" style={{ maxWidth: '1200px', margin: '0 auto', paddingLeft: '20px', paddingRight: '20px', boxSizing: 'border-box', width: '100%' }}>
            {/* 左侧：标题和描述 */}
            <div className="elementor-column elementor-col-50 elementor-top-column elementor-element elementor-element-4826012" data-id="4826012" data-element_type="column">
              <div className="elementor-widget-wrap elementor-element-populated">
                <div className="elementor-element elementor-element-cd16e2f elementor-widget elementor-widget-heading" data-id="cd16e2f" data-element_type="widget" data-widget_type="heading.default">
                  <div className="elementor-widget-container">
                    <h2 className="elementor-heading-title elementor-size-default" style={{ fontSize: '32px', fontWeight: 600, lineHeight: '1.3em', color: '#FFFFFF' }}>GET an Instant Quote</h2>
                  </div>
                </div>
                <div className="elementor-element elementor-element-a1e2304 elementor-widget__width-initial elementor-widget elementor-widget-text-editor" data-id="a1e2304" data-element_type="widget" data-widget_type="text-editor.default" style={{ textAlign: 'left' }}>
                  <div className="elementor-widget-container" style={{ textAlign: 'left' }}>
                    <p style={{ textAlign: 'left' }}>Our technical staff is available 24/7 for support and fast response, giving full advice on your products and sharing engineering knowledge. Whether you order or not, we're always here to support your business.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 右侧：联系表单 */}
            <div className="elementor-column elementor-col-50 elementor-top-column elementor-element elementor-element-f8d20b2" data-id="f8d20b2" data-element_type="column">
              <div className="elementor-widget-wrap elementor-element-populated">
                <div className="elementor-element elementor-element-889859a elementor-widget elementor-widget-heading" data-id="889859a" data-element_type="widget" data-widget_type="heading.default" style={{ color: '#FFFFFF' }}>
                  <div className="elementor-widget-container" style={{ color: '#FFFFFF' }}>
                    <h3 className="elementor-heading-title elementor-size-default" style={{ color: '#FFFFFF', WebkitTextFillColor: '#FFFFFF' }}>Send Us Email</h3>
                  </div>
                </div>
                <div className="elementor-element elementor-element-fb6aa31 elementor-widget__width-auto elementor-widget elementor-widget-text-editor" data-id="fb6aa31" data-element_type="widget" data-widget_type="text-editor.default">
                  <div className="elementor-widget-container">
                    <p>Get an instant quote from our most experienced consultants.</p>
                  </div>
                </div>
                <ContactForm />
              </div>
            </div>
          </div>
        </section>

        {/* 第二部分：四个列（About Us, Product Center, News Center, Contact Us） */}
        <section className="elementor-section elementor-top-section elementor-element elementor-element-be8562e elementor-section-boxed elementor-section-height-default elementor-section-height-default" data-id="be8562e" data-element_type="section" data-settings='{"background_background":"classic"}' style={{ backgroundColor: '#2A2A2A', background: '#2A2A2A', backgroundImage: 'none', width: '100%', maxWidth: '100%', minWidth: '100%' }}>
          <div className="elementor-container elementor-column-gap-default" style={{ maxWidth: '1200px', margin: '0 auto', paddingLeft: '20px', paddingRight: '20px', boxSizing: 'border-box', width: '100%' }}>
            <div className="elementor-column elementor-col-100 elementor-top-column elementor-element elementor-element-2436317" data-id="2436317" data-element_type="column" style={{ paddingLeft: '0', paddingRight: '0' }}>
              <div className="elementor-widget-wrap elementor-element-populated">
                <section className="elementor-section elementor-inner-section elementor-element elementor-element-858a311 elementor-section-boxed elementor-section-height-default elementor-section-height-default" data-id="858a311" data-element_type="section" style={{ maxWidth: '100%', width: '100%', paddingLeft: '0', paddingRight: '0', marginLeft: '0', marginRight: '0' }}>
                  <div className="elementor-container elementor-column-gap-default" style={{ maxWidth: '100%', width: '100%', paddingLeft: '0', paddingRight: '0', marginLeft: '0', marginRight: '0' }}>
                    {/* About Us 列 */}
                    <div className="elementor-column elementor-col-25 elementor-inner-column elementor-element elementor-element-49d155c" data-id="49d155c" data-element_type="column">
                      <div className="elementor-widget-wrap elementor-element-populated">
                        <div className="elementor-element elementor-element-010a499 elementor-widget elementor-widget-heading" data-id="010a499" data-element_type="widget" data-widget_type="heading.default">
                          <div className="elementor-widget-container">
                            <h4 className="elementor-heading-title elementor-size-default">{settingsData.footer_about_title || 'About Us'}</h4>
                          </div>
                        </div>
                        <div className="elementor-element elementor-element-fcaa0b3 elementor-nav-menu--dropdown-none elementor-widget elementor-widget-nav-menu" data-id="fcaa0b3" data-element_type="widget" data-settings='{"layout":"vertical","submenu_icon":{"value":"<i class=\"fas fa-caret-down\"></i>","library":"fa-solid"}}' data-widget_type="nav-menu.default">
                          <div className="elementor-widget-container">
                            <nav aria-label="Menu" className="elementor-nav-menu--main elementor-nav-menu__container elementor-nav-menu--layout-vertical e--pointer-text e--animation-none">
                              <ul id="menu-1-fcaa0b3" className="elementor-nav-menu sm-vertical">
                                {aboutUsChildren.length > 0 ? (
                                  aboutUsChildren.map((item: any) => (
                                    <li key={item.id} className="menu-item menu-item-type-post_type menu-item-object-page menu-item-338">
                                      <Link href={item.url || '#'} className="elementor-item menu-link">{item.title}</Link>
                                    </li>
                                  ))
                                ) : (
                                  <>
                                    <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-338">
                                      <Link href="/about-us" className="elementor-item menu-link">About Us</Link>
                                    </li>
                                    <li className="menu-item menu-item-type-post_type menu-item-object-page menu-item-341">
                                      <Link href="/contact-us" className="elementor-item menu-link">Contact Us</Link>
                                    </li>
                                  </>
                                )}
                              </ul>
                            </nav>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Product Center 列 */}
                    <div className="elementor-column elementor-col-25 elementor-inner-column elementor-element elementor-element-530277a" data-id="530277a" data-element_type="column">
                      <div className="elementor-widget-wrap elementor-element-populated">
                        <div className="elementor-element elementor-element-92e87aa elementor-widget elementor-widget-heading" data-id="92e87aa" data-element_type="widget" data-widget_type="heading.default">
                          <div className="elementor-widget-container">
                            <h4 className="elementor-heading-title elementor-size-default">{settingsData.footer_product_title || 'Product Center'}</h4>
                          </div>
                        </div>
                        <div className="elementor-element elementor-element-23dc2fd elementor-widget elementor-widget-ucaddon_simple_list_menu" data-id="23dc2fd" data-element_type="widget" data-widget_type="ucaddon_simple_list_menu.default">
                          <div className="elementor-widget-container">
                            <div id="uc_simple_list_menu_elementor_23dc2fd" className="simple_list_menu">
                              <ul id="menu-product-categories-1" className="uc-list-menu">
                                {topLevelCategories.map((category: any) => (
                                  <li key={category.id} className="menu-item menu-item-type-taxonomy menu-item-object-products menu-item-662">
                                    <Link href={`/products?category=${category.slug}`} className="menu-link">{category.name}</Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* News Center 列 */}
                    <div className="elementor-column elementor-col-25 elementor-inner-column elementor-element elementor-element-b9c1ede" data-id="b9c1ede" data-element_type="column">
                      <div className="elementor-widget-wrap elementor-element-populated">
                        <div className="elementor-element elementor-element-99e8265 elementor-widget elementor-widget-heading" data-id="99e8265" data-element_type="widget" data-widget_type="heading.default">
                          <div className="elementor-widget-container">
                            <h4 className="elementor-heading-title elementor-size-default">{settingsData.footer_news_title || 'News Center'}</h4>
                          </div>
                        </div>
                        <div className="elementor-element elementor-element-61c4648 elementor-nav-menu--dropdown-none elementor-widget elementor-widget-nav-menu" data-id="61c4648" data-element_type="widget" data-settings='{"layout":"vertical","submenu_icon":{"value":"<i class=\"fas fa-caret-down\"></i>","library":"fa-solid"}}' data-widget_type="nav-menu.default">
                          <div className="elementor-widget-container">
                            <nav aria-label="Menu" className="elementor-nav-menu--main elementor-nav-menu__container elementor-nav-menu--layout-vertical e--pointer-text e--animation-none">
                              <ul id="menu-1-61c4648" className="elementor-nav-menu sm-vertical">
                                {newsChildren.length > 0 ? (
                                  newsChildren.map((item: any) => (
                                    <li key={item.id} className="menu-item menu-item-type-taxonomy menu-item-object-category menu-item-343">
                                      <Link href={item.url || '#'} className="elementor-item menu-link">{item.title}</Link>
                                    </li>
                                  ))
                                ) : (
                                  <>
                                    <li className="menu-item menu-item-type-taxonomy menu-item-object-category menu-item-343">
                                      <Link href="/news" className="elementor-item menu-link">News</Link>
                                    </li>
                                  </>
                                )}
                              </ul>
                            </nav>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Contact Us 列 */}
                    <div className="elementor-column elementor-col-25 elementor-inner-column elementor-element elementor-element-7b56fa2" data-id="7b56fa2" data-element_type="column">
                      <div className="elementor-widget-wrap elementor-element-populated">
                        <div className="elementor-element elementor-element-1437ab9 elementor-widget elementor-widget-heading" data-id="1437ab9" data-element_type="widget" data-widget_type="heading.default">
                          <div className="elementor-widget-container">
                            <h4 className="elementor-heading-title elementor-size-default">{settingsData.footer_contact_title || 'Contact Us'}</h4>
                          </div>
                        </div>
                        <div className="elementor-element elementor-element-b3b6520 elementor-icon-list--layout-traditional elementor-list-item-link-full_width elementor-widget elementor-widget-icon-list" data-id="b3b6520" data-element_type="widget" data-widget_type="icon-list.default">
                          <div className="elementor-widget-container">
                            <ul className="elementor-icon-list-items">
                              <li className="elementor-icon-list-item">
                                <span className="elementor-icon-list-text">Addr: {settingsData.footer_address || 'Industrial Park, Technological Development Zone'}</span>
                              </li>
                              <li className="elementor-icon-list-item">
                                <span className="elementor-icon-list-text">Tel: {settingsData.footer_phone || settingsData.header_phone || '0086-731-84111111'}</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* 第三部分：版权信息和社交媒体 */}
                <section className="elementor-section elementor-inner-section elementor-element elementor-element-30b73b9 elementor-section-boxed elementor-section-height-default elementor-section-height-default" data-id="30b73b9" data-element_type="section">
                  <div className="elementor-container elementor-column-gap-default">
                    {/* 版权信息 */}
                    <div className="elementor-column elementor-col-50 elementor-inner-column elementor-element elementor-element-f92abed" data-id="f92abed" data-element_type="column">
                      <div className="elementor-widget-wrap elementor-element-populated">
                        <div className="elementor-element elementor-element-1365814 elementor-widget elementor-widget-heading" data-id="1365814" data-element_type="widget" data-widget_type="heading.default">
                          <div className="elementor-widget-container">
                            <div className="elementor-heading-title elementor-size-default">Copyright © {new Date().getFullYear()} {settingsData.site_name || 'All Rights Reserved'}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 社交媒体分享按钮 */}
                    <div className="elementor-column elementor-col-50 elementor-inner-column elementor-element elementor-element-4e42bb0" data-id="4e42bb0" data-element_type="column">
                      <div className="elementor-widget-wrap elementor-element-populated">
                        <div className="elementor-element elementor-element-8b307d2 elementor-share-buttons--view-icon elementor-share-buttons--skin-flat elementor-share-buttons--shape-circle elementor-grid-0 elementor-share-buttons--color-official elementor-widget elementor-widget-share-buttons" data-id="8b307d2" data-element_type="widget" data-widget_type="share-buttons.default">
                          <div className="elementor-widget-container">
                            <div className="elementor-grid">
                              <div className="elementor-grid-item">
                                <div className="elementor-share-btn elementor-share-btn_facebook" role="button" tabIndex={0} aria-label="Share on facebook">
                                  <span className="elementor-share-btn__icon">
                                    <i className="fab fa-facebook" aria-hidden="true"></i>
                                  </span>
                                </div>
                              </div>
                              <div className="elementor-grid-item">
                                <div className="elementor-share-btn elementor-share-btn_twitter" role="button" tabIndex={0} aria-label="Share on twitter">
                                  <span className="elementor-share-btn__icon">
                                    <i className="fab fa-twitter" aria-hidden="true"></i>
                                  </span>
                                </div>
                              </div>
                              <div className="elementor-grid-item">
                                <div className="elementor-share-btn elementor-share-btn_linkedin" role="button" tabIndex={0} aria-label="Share on linkedin">
                                  <span className="elementor-share-btn__icon">
                                    <i className="fab fa-linkedin" aria-hidden="true"></i>
                                  </span>
                                </div>
                              </div>
                              <div className="elementor-grid-item">
                                <div className="elementor-share-btn elementor-share-btn_pinterest" role="button" tabIndex={0} aria-label="Share on pinterest">
                                  <span className="elementor-share-btn__icon">
                                    <i className="fab fa-pinterest" aria-hidden="true"></i>
                                  </span>
                                </div>
                              </div>
                              <div className="elementor-grid-item">
                                <div className="elementor-share-btn elementor-share-btn_skype" role="button" tabIndex={0} aria-label="Share on skype">
                                  <span className="elementor-share-btn__icon">
                                    <i className="fab fa-skype" aria-hidden="true"></i>
                                  </span>
                                </div>
                              </div>
                              <div className="elementor-grid-item">
                                <div className="elementor-share-btn elementor-share-btn_telegram" role="button" tabIndex={0} aria-label="Share on telegram">
                                  <span className="elementor-share-btn__icon">
                                    <i className="fab fa-telegram" aria-hidden="true"></i>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </section>
      </div>
    </footer>
  );
}

// 联系表单组件
function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitMessage(null);
    
    // 验证表单
    if (!formData.name.trim()) {
      setSubmitMessage({ type: 'error', text: '请输入姓名' });
      return;
    }
    if (!formData.email.trim()) {
      setSubmitMessage({ type: 'error', text: '请输入邮箱' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setSubmitMessage({ type: 'error', text: '请输入有效的邮箱地址' });
      return;
    }
    if (!formData.message.trim()) {
      setSubmitMessage({ type: 'error', text: '请输入留言内容' });
      return;
    }

    setIsSubmitting(true);
    try {
      await contactAPI.create({
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
      });
      setSubmitMessage({ type: 'success', text: '留言提交成功，我们会尽快与您联系！' });
      // 清空表单
      setFormData({
        name: '',
        email: '',
        message: '',
      });
      // 3秒后清除成功消息
      setTimeout(() => setSubmitMessage(null), 3000);
    } catch (error: any) {
      console.error('提交留言失败:', error);
      const errorMessage = error?.response?.data?.message || error?.message || '提交失败，请稍后重试';
      setSubmitMessage({ type: 'error', text: errorMessage });
      // 5秒后清除错误消息
      setTimeout(() => setSubmitMessage(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="elementor-element elementor-element-51e44c4 elementor-button-align-start elementor-widget elementor-widget-form" data-id="51e44c4" data-element_type="widget" data-settings='{"step_next_label":"Next","step_previous_label":"Previous","button_width":"100","step_type":"number_text","step_icon_shape":"circle"}' data-widget_type="form.default">
      <style dangerouslySetInnerHTML={{
        __html: `
          #form-field-name,
          #form-field-email,
          #form-field-message {
            color: #000000 !important;
          }
          #form-field-name::placeholder,
          #form-field-email::placeholder,
          #form-field-message::placeholder {
            color: #666666 !important;
            opacity: 1;
          }
        `
      }} />
      <div className="elementor-widget-container">
        {submitMessage && (
          <div
            style={{
              padding: '12px 16px',
              marginBottom: '16px',
              borderRadius: '4px',
              backgroundColor: submitMessage.type === 'success' ? '#f6ffed' : '#fff2f0',
              border: `1px solid ${submitMessage.type === 'success' ? '#b7eb8f' : '#ffccc7'}`,
              color: submitMessage.type === 'success' ? '#52c41a' : '#ff4d4f',
              fontSize: '14px',
            }}
          >
            {submitMessage.text}
          </div>
        )}
        <form className="elementor-form" onSubmit={handleSubmit}>
          <div className="elementor-form-fields-wrapper elementor-labels-">
            <div className="elementor-field-type-text elementor-field-group elementor-column elementor-field-group-name elementor-col-100">
              <label htmlFor="form-field-name" className="elementor-field-label elementor-screen-only">Name</label>
              <input
                size={1}
                type="text"
                name="name"
                id="form-field-name"
                className="elementor-field elementor-size-sm elementor-field-textual"
                placeholder="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={isSubmitting}
                style={{ color: '#000000' }}
              />
            </div>
            <div className="elementor-field-type-email elementor-field-group elementor-column elementor-field-group-email elementor-col-100 elementor-field-required">
              <label htmlFor="form-field-email" className="elementor-field-label elementor-screen-only">Email</label>
              <input
                size={1}
                type="email"
                name="email"
                id="form-field-email"
                className="elementor-field elementor-size-sm elementor-field-textual"
                placeholder="Email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={isSubmitting}
                style={{ color: '#000000' }}
              />
            </div>
            <div className="elementor-field-type-textarea elementor-field-group elementor-column elementor-field-group-message elementor-col-100">
              <label htmlFor="form-field-message" className="elementor-field-label elementor-screen-only">Message</label>
              <textarea
                className="elementor-field-textual elementor-field elementor-size-sm"
                name="message"
                id="form-field-message"
                rows={4}
                placeholder="Message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                disabled={isSubmitting}
                style={{ color: '#000000' }}
              ></textarea>
            </div>
            <div className="elementor-field-group elementor-column elementor-field-type-submit elementor-col-100 e-form__buttons" style={{ width: '15%', maxWidth: '15%', minWidth: '15%', flex: '0 0 15%' }}>
              <button
                className="elementor-button elementor-size-sm"
                type="submit"
                disabled={isSubmitting}
                style={{ backgroundColor: 'red', background: 'red', borderColor: 'red', opacity: isSubmitting ? 0.6 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
              >
                <span className="elementor-button-content-wrapper">
                  <span className="elementor-button-text">{isSubmitting ? '发送中...' : 'Send'}</span>
                </span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
